import { useState } from "react";
import { Form, TextInput, useNotify, required } from "react-admin";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useParams } from "react-router-dom";
import { PublicAuthShell, AuthAltLinks } from "./PublicAuthShell";
import { PUBLIC_REGISTRATION_ENABLED } from "../../data/features";

// Tela pública de recuperação de senha. Dois passos no mesmo componente,
// decididos pelo token da URL:
//  - sem token (/account-recovery): pede o e-mail e dispara o link;
//  - com token (/account-recovery/:token): define a nova senha.
// Os endpoints e payloads são os herdados — aqui só muda a interface.
export const RecoveryPasswordPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { token } = useParams();
  const notify = useNotify();

  const handleSubmitToken = async (data: any) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_PATH}/auth/forgot-password`,
        {
          method: "POST",
          body: JSON.stringify(data),
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) {
        notify("Erro ao recuperar senha.", { type: "error" });
      }
      if (response.status >= 200 && response.status < 300) {
        notify("Senha atualizada com sucesso!", { type: "success" });
        setTimeout(() => {
          //@ts-ignore
          window.location = "/#/login";
        }, 3000);
      }
    } catch (error: any) {
      notify("Erro ao recuperar senha.", { type: "error" });
    }
  };

  const handleSubmitMail = async (data: any) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_PATH}/auth/reset-password`,
        {
          method: "POST",
          body: JSON.stringify(data),
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) {
        notify("Erro ao recuperar senha.", { type: "error" });
      }
      if (response.status >= 200 && response.status < 300) {
        notify(
          "Solicitação de recuperação de senha enviada. Confira seu e-mail.",
          { type: "success" }
        );
        setTimeout(() => {
          //@ts-ignore
          window.location = "/#/login";
        }, 3000);
      }
    } catch (error: any) {
      notify("Erro ao solicitar recuperação de senha.", { type: "error" });
    }
  };

  const isTokenStep = Boolean(token);
  return (
    <PublicAuthShell
      title={isTokenStep ? "Criar nova senha" : "Recuperar senha"}
      subtitle={
        isTokenStep
          ? "Defina a nova senha da sua conta."
          : "Informe seu e-mail e enviaremos um link para redefinir sua senha."
      }>
      {isTokenStep ? (
        <Form
          onSubmit={async (data) => {
            setIsLoading(true);
            await handleSubmitToken({ token, password: data.password });
            setIsLoading(false);
          }}>
          <TextInput
            source="password"
            label="Nova senha"
            type={showPassword ? "text" : "password"}
            fullWidth
            validate={required()}
            helperText={false}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showPassword ? "Ocultar senha" : "Mostrar senha"
                    }
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end">
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            disabled={isLoading}
            variant="contained"
            color="primary"
            sx={{ height: 48, marginTop: 1.5 }}>
            {isLoading && (
              <CircularProgress
                sx={{ color: "white", marginRight: 1 }}
                size={18}
              />
            )}
            Salvar nova senha
          </Button>
        </Form>
      ) : (
        <Form
          onSubmit={async (data) => {
            setIsLoading(true);
            await handleSubmitMail(data);
            setIsLoading(false);
          }}>
          <TextInput
            source="email"
            label="E-mail"
            type="email"
            fullWidth
            validate={required()}
            helperText={false}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={isLoading}
            sx={{ height: 48, marginTop: 1.5 }}>
            {isLoading && (
              <CircularProgress
                sx={{ color: "white", marginRight: 1 }}
                size={18}
              />
            )}
            Enviar link de recuperação
          </Button>
        </Form>
      )}
      <AuthAltLinks
        links={[
          {
            action: "Voltar para o login",
            href: window.location.origin + "/#/login",
          },
          // Some enquanto o cadastro estiver desativado (data/features.ts)
          ...(PUBLIC_REGISTRATION_ENABLED
            ? [
                {
                  text: "Não tem conta?",
                  action: "Cadastre-se",
                  href: window.location.origin + "/#/register",
                },
              ]
            : []),
        ]}
      />
    </PublicAuthShell>
  );
};
